/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary, consistent-return */
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getToolName, isToolUIPart, type UIMessage } from 'ai';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import { formatRelative } from '../services/chat-storage';
import type { RenderBlock } from '../services/ai-agent.service';
import MarkdownText from '../components/Data/Markdown';
import AgentBlock, {
  ChartSettingsProvider,
  DEFAULT_CHART_SETTINGS
} from '../components/AiAgent/AgentBlocks';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';
const BASE = `${DEV_NEW_DOMAIN_URL}/ai/admin/chats`;

interface AdminSessionSummary {
  id: string;
  username: string;
  clientId: number;
  title: string;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
}

interface FullSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: UIMessage[];
  /** Render blocks (charts/cards) keyed by the tool call that produced them. */
  displayMap: Record<string, RenderBlock[]>;
}

// Pull the concatenated text of a message's text parts (empty if none).
function messageText(msg: UIMessage): string {
  return (msg.parts || [])
    .filter((p) => p.type === 'text')
    .map((p) => (p as { text?: string }).text || '')
    .join('\n')
    .trim();
}

// Render one transcript message exactly as the live chat does: user turns as a
// right-aligned bubble, assistant turns with markdown text and the actual
// charts/cards (looked up from displayMap by the producing tool call).
function MessageView({
  message,
  displayMap
}: {
  message: UIMessage;
  displayMap: Record<string, RenderBlock[]>;
}): ReactElement {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex flex-col items-end gap-1">
        <span className="text-[10px] uppercase tracking-wide text-gray-500">user</span>
        <div className="max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary-500/20 px-3 py-2 text-sm text-gray-100">
          {messageText(message)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <span className="text-[10px] uppercase tracking-wide text-gray-500">{message.role}</span>
      <div className="w-full">
        {(message.parts || []).map((part, i) => {
          if (part.type === 'text') {
            const value = (part as { text?: string }).text;
            if (!value) return null;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="prose prose-invert max-w-none text-sm leading-relaxed">
                <MarkdownText>{value}</MarkdownText>
              </div>
            );
          }
          if (isToolUIPart(part)) {
            if (part.state === 'output-error') {
              return (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="my-2 rounded-lg border border-error-500/30 bg-error-500/10 px-3 py-2 text-sm text-error-300"
                >
                  {getToolName(part)} failed: {part.errorText || 'unknown error'}
                </div>
              );
            }
            const blocks = displayMap[part.toolCallId];
            if (!blocks || !blocks.length) return null;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={i}>
                {blocks.map((b, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <AgentBlock key={j} block={b} />
                ))}
              </Fragment>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

/**
 * Admin-only page: browse every user's Insight Agent sessions. Access is gated
 * both client-side (redirect) and server-side (the /ai/admin/* API returns 403
 * to anyone but the admin), so this is a convenience view, not the security
 * boundary.
 * @returns The admin AI-sessions page.
 */
export default function AdminAiSessionsPage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [sessions, setSessions] = useState<AdminSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FullSession | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const { data } = await axiosInstance.get<AdminSessionSummary[]>(BASE);
        if (cancelled) return;
        setSessions(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load sessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAllowed]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);
    const load = async (): Promise<void> => {
      try {
        const { data } = await axiosInstance.get<FullSession>(`${BASE}/${selectedId}`);
        if (!cancelled) setDetail(data || null);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const totalMessages = sessions.reduce((acc, s) => acc + (s.messageCount || 0), 0);
  const uniqueUsers = new Set(sessions.map((s) => s.username)).size;

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">AI Agent Sessions (Admin)</h1>
          <p className="text-sm text-gray-400">Signed in as {userEmail}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Sessions</div>
            <div className="text-xl font-semibold">{sessions.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Users</div>
            <div className="text-xl font-semibold">{uniqueUsers}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Messages</div>
            <div className="text-xl font-semibold">{totalMessages}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-icons-outlined animate-spin">autorenew</span>
          Loading…
        </div>
      ) : error ? (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(320px,420px)_1fr]">
          {/* Sessions list */}
          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">All Sessions</h2>
              <span className="text-sm text-gray-400">{sessions.length} total</span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="px-4 py-6 text-center text-gray-500">No sessions yet</div>
              ) : (
                <ul>
                  {sessions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(s.id)}
                        className={`flex w-full flex-col gap-1 border-b border-gray-800/60 px-4 py-3 text-left transition hover:bg-background-500 ${
                          selectedId === s.id ? 'bg-background-500' : ''
                        }`}
                      >
                        <div className="truncate text-sm font-medium text-gray-100">
                          {s.title || 'Untitled chat'}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="truncate">{s.username}</span>
                          <span className="ml-2 shrink-0">
                            {formatRelative(s.updatedAt)} · {s.messageCount} msg
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Transcript */}
          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">
                {detail ? detail.title || 'Untitled chat' : 'Transcript'}
              </h2>
              {detail && (
                <p className="mt-0.5 text-xs text-gray-500 font-mono">{detail.id}</p>
              )}
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
              {!selectedId ? (
                <div className="py-10 text-center text-gray-500">
                  Select a session to view its transcript
                </div>
              ) : detailLoading ? (
                <div className="flex items-center gap-3 text-gray-400">
                  <span className="material-icons-outlined animate-spin">autorenew</span>
                  Loading transcript…
                </div>
              ) : !detail || detail.messages.length === 0 ? (
                <div className="py-10 text-center text-gray-500">Empty session</div>
              ) : (
                <ChartSettingsProvider value={DEFAULT_CHART_SETTINGS}>
                  <div className="flex flex-col gap-5">
                    {detail.messages.map((m, i) => (
                      <MessageView
                        // eslint-disable-next-line react/no-array-index-key
                        key={m.id || i}
                        message={m}
                        displayMap={detail.displayMap || {}}
                      />
                    ))}
                  </div>
                </ChartSettingsProvider>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
